// Re-export types from other files within the types directory

// --- Core Entity Types ---

export interface Plan {
  id: number; // Or string
  name: string;
  description?: string | null;
  price_monthly: number; // Assuming number, adjust if string
  price_yearly?: number | null;
  paddle_product_id?: string | null;
  paddle_price_id?: string | null; // This is often what you use for Paddle checkout
  features?: Record<string, string | number | boolean> | string[] | null; // More flexible features
  trial_days?: number | null; // << MAKE THIS OPTIONAL (if it was trial_period_days, adjust the name)
  active: boolean;
  user_type?: string; // Or your UserTypeEnum
  order?: number;
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELED = 'canceled',
  UNPAID = 'unpaid',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAUSED = 'paused', // Added if you use it
  // Add any other statuses your backend might send
}

export interface Subscription {
  id: string; // Or number, depending on your backend
  user_id: string; // Or number
  plan_id: number; // Or string, ensure it matches Plan['id'] type
  paddle_subscription_id?: string | null;
  status: SubscriptionStatus;
  trial_ends_at?: string | null; // Date string from backend
  starts_at?: string | null;     // Date string from backend
  ends_at?: string | null;       // Date string from backend - THIS WAS MISSING
  cancel_at_period_end?: boolean | null;
  current_period_starts?: string | null; // Date string from backend
  current_period_end?: string | null; // Date string from backend
  next_billing_at?: string | null; // Date string from backend
  cancelled_at?: string | null;  // Date string from backend
  created_at: string;          // Date string from backend
  updated_at: string;          // Date string from backend
  provider?: string | null;      // e.g., 'paddle', 'stripe'
  plan_placeholder_id?: string; // New property added
}

export interface SubscriptionTransaction {
  id: string;
  subscription_id: string;
  amount: number;
  status: string; // e.g., 'completed', 'failed', 'pending'
  provider: string; // e.g., 'paddle', 'stripe'
  description: string;
  transaction_date: string; // ISO date string
  payment_method?: string;
  transaction_type?: string; // e.g., 'charge', 'refund'
}

// --- Team Management Types ---

export interface Team {
  id: string;
  name: string;
  enterprise_user_id: string; // User ID of the team owner/creator
  custom_free_reports: number;
  custom_free_competitor_reports: number;
  created_at: string; // ISO date string
  updated_at: string; // ISO date string
  member_count?: number; // Often calculated in backend queries
}

export interface TeamMember {
  id: string; // User ID of the member
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  joined_at: string; // ISO date string (from team_members table)
}

// --- Standardized API Response Wrapper ---

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string; // For error codes or specific error messages
  updatedBalance?: number; // If some operations affect a credit balance
}

// --- Specific Data Payloads for API Responses (used as T in ApiResponse<T>) ---
// These are useful when the 'data' field of ApiResponse itself has a specific structure.
// For simple array returns like GET /subscriptions or GET /subscriptions/plans,
// T would be Subscription[] or Plan[] directly.

export interface SingleSubscriptionData {
  subscription: Subscription;
}

export interface TeamListData {
  teams: Team[];
}

export interface TeamMemberListData {
  members: TeamMember[];
}

// --- Data Payloads for Mutation Operations (used as T in ApiResponse<T>) ---

export interface CreateSubscriptionData {
  transactionId?: string; // For Paddle, if checkout is initiated
  approvalUrl?: string;   // For PayPal, if redirect is needed
  checkoutUrl?: string;   // For Stripe, if redirect is needed
  subscriptionId?: string; // The ID of the newly created subscription
  provider?: string;
  subscription?: Subscription; // Optionally return the full new subscription

  // Add these aliases used by Paddle
  paddle_transaction_id?: string;
  paddle_subscription_id?: string;
}

export interface UpdateSubscriptionData {
  transactionId?: string; // For Paddle, if a payment is involved in plan change
  approvalUrl?: string;
  checkoutUrl?: string;
  provider?: string;
  subscription?: Subscription; // Return the updated subscription details

  // New fields for reactivate_with_new_plan action
  new_transaction_id?: string;
  new_subscription_id?: string;
  old_subscription_id?: string;

  // Additional fields that might be in responses
  message?: string;

  // Add Paddle-specific aliases
  paddle_transaction_id?: string;
  paddle_subscription_id?: string;
}

export interface TeamCreateData extends Team { } // Response after creating a team

export interface TeamMemberAddData { // Response after adding a member
  message: string;
  userId: string; // ID of the added member
}

export interface TransactionHistoryData {
  transactions: SubscriptionTransaction[];
}

// --- Paddle Types ---

// Define the PaddleCheckout interface
export interface PaddleCheckout {
  open: (options: {
    transactionId: string;
    eventCallback?: (eventData: PaddleEventData) => void;
    [key: string]: any;
  }) => void;
  close?: () => void;
}

// Define the PaddleEventData interface
export interface PaddleEventData {
  name: string; // checkout.loaded, checkout.completed, checkout.closed, etc.
  data?: {
    checkout?: {
      id?: string;
      status?: string;
    };
    subscription?: {
      id?: string;
      user_id?: string;
      plan_id?: string;
      status?: string;
    };
    status?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// Extend the global Window interface
declare global {
  interface Window {
    dispatchSubscriptionOperationCompleted?: () => void;
    Paddle?: {
      Environment: {
        set: (env: string) => void;
      };
      Initialize: (options: {
        token: string;
        eventCallback?: (eventData: any) => void;
        [key: string]: any;
      }) => void;
      Checkout: PaddleCheckout;
      [key: string]: any;
    };
  }
}

// --- Task Management Types ---
export type TaskStatus = 'open' | 'in_progress' | 'resolved' | 'closed' | 'needs_review' | 'deleting' | 'failed';
export type TaskPriority = 'low' | 'medium' | 'high';
export type TaskType = 'custom' | 'monitoring' | 'log_analysis' | 'security' | 'image_optimization' | 'agent';

export interface Task {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  status: TaskStatus;
  priority: 'low' | 'medium' | 'high' | number;
  title?: string;
  description?: string | null;
  type?: 'monitoring' | 'log_analysis' | 'security' | 'custom' | 'image_optimization' | 'agent';
  parent_task_id?: string | null; // For agent sub-tasks
  completed_at?: string | null;
  team_id?: string | null;
  selected_solution?: string | null; // Changed from selected_solution_id to match DB schema
  github_repo?: string | null;
  github_issue_id?: number | null;
  github_issue_url?: string | null;
  github_pr_id?: number | null;
  github_commit_sha?: string | null;
  github_selected_solution?: string | null; // Solution that was used when creating/updating GitHub issue
  tag?: string | null; // <-- Add tag property
  context?: any; // <-- Add context property for all context fields
  notes?: string | null; // For legacy compatibility
  created_by?: {
    email?: string | null;
    label?: string | null;
  };
  has_ai_history?: boolean; // Added for direct-to-chat UX
  source_id?: string | null; // Added for monitoring tasks
  has_monitoring_history?: boolean; // Added for monitoring tasks
}

export type TaskCreatePayload = Omit<Task, 'id' | 'user_id' | 'team_id' | 'created_at' | 'updated_at' | 'issue_description' | 'notes' | 'task_type' | 'page_url' | 'website_url' | 'selected_solution'> & {
  // Allow old field names in create payload for backward compatibility
  issue_description?: string;
  notes?: string;
  task_type?: TaskType | string; // Allow backward compatibility
};

export interface AiSolution {
  id: string;
  task_id: string;
  solution_title: string;
  solution_description?: string;
  solution_data?: AiSolutionData[]; // Array of solution objects stored in JSONB
  created_at: string;
}

// Individual solution object stored in the solution_data JSONB array
export interface AiSolutionData {
  ai_request_id: string; // References ai_requests.id
  solution: string; // The actual AI-generated solution text
  type: 'candidate' | 'selected'; // Current status of this solution
}

export * from './reports';


// --- Monitoring Types ---
export interface MonitoredUrl {
  id: string;
  user_id: string;
  website_url: string;
  created_at: string;
  scan_in_progress?: boolean; // Added to track if a scan is currently running
  is_active?: boolean;
  additional_metrics?: any;
  // These come from the LEFT JOIN with tasks
  title?: string | null;
  description?: string | null;
  has_reached_notification_limit?: boolean;
  latest_task_id?: string | null; // Added to link to the latest associated task
}

export interface MonitoringHistory {
  id: string;
  monitoring_id: string; // Foreign key to MonitoredUrl
  scan_date: string;
  status: 'success' | 'failed';
  // Metrics are now at root level (flat structure from monitoring_lab_history table)
  performance_score: number | null;
  lcp: number | null;
  cls: number | null;
  tti: number | null;
  fcp: number | null;
  ttfb: number | null;
  tbt: number | null;
  speed_index?: number | null;
  // Diagnostics remain as nested object
  diagnostics?: {
    render_blocking_resources?: any[];
    mainthread_work_breakdown?: any[];
    lcp_element_selector?: string | null;
    unused_javascript?: any[];
    unused_css?: any[];
  };
}

// Flattened version returned by GET /monitoring/:id API
export interface FlattenedMonitoringHistory {
  id: string;
  monitoring_id: string;
  scan_date: string;
  status: 'success' | 'failed';
  // Metrics flattened to root level
  performance_score: number | null;
  lcp: number | null;
  cls: number | null;
  tti: number | null;
  fcp: number | null;
  ttfb: number | null;
  tbt: number | null;
  // Diagnostics
  diagnostics?: {
    render_blocking_resources?: any[];
    mainthread_work_breakdown?: any[];
    lcp_element_selector?: string | null;
    unused_javascript?: any[];
    unused_css?: any[];
  };
}

export interface MonitoringUsageResponse {
  canCreate: {
    monitoring: boolean;
    monitoringReason?: string;
    costInCredits?: number;
    userCreditBalance?: number;
    hasSufficientCredits?: boolean;
    // List of already monitored URL + formFactor combinations for duplicate detection
    existingMonitoredUrls?: Array<{ url: string; formFactor: string }>;
  };
  featureKey?: string;
  consumedInPeriod?: number;
  limitForPeriod?: number;
  remainingInBundle?: number;
  overageCostPerUnit?: number;
  subscriptionStatus?: string; // 'trialing' | 'active' | etc.
  metricsUsage?: {
    featureKey: string;
    consumedInPeriod: number;
    limitForPeriod: number;
    remainingInBundle: number;
    overageCostPerUnit?: number;
  };
  accessContext?: {
    billingUserId: string;
    teamId?: string | null;
    teamName?: string | null;
    planFeatures?: any;
    plan_user_type_granted?: string | null;
  };
}

// --- Monitoring Insights ---

export interface Insight {
  id: string;
  type: 'warning' | 'info' | 'success';
  severity: 'low' | 'medium' | 'high';
  metric: string;
  title: string;
  description: string;
  trend?: 'improving' | 'degrading' | 'stable';
  linkedDiagnostics?: {
    type: string;
    items: any[];
  };
  value?: number;
  threshold?: number;
  percentChange?: number;
}

export interface MetricStats {
  avg: number;
  min: number;
  max: number;
  latest: number | null;
  trend: number | null;
  dataPoints: number;
}
